import type { BillFieldsFragment } from '@/types/operations';

/**
 * The bill shape these readers need: the BillFields selection every billing
 * surface already fetches. Deliberately the operation type rather than the
 * schema's Bill — a view holds what its query returned, and the schema type
 * demands fields no selection set carries.
 */
type BillLike = BillFieldsFragment;

/**
 * Who a bill is raised against, in words a person can act on.
 *
 * An application fee is billed against the FormSubmission somebody filled in,
 * so its `billable` is neither a student nor a cohort. With no branch for that
 * shape the bills list fell through to its last resort and printed the morph
 * row number — "ID: 1936" over "FormSubmission" — against real money, with
 * nothing naming the applicant or the form they applied on.
 *
 * Applicants have no student record, so there is no name column to read: the
 * submission's own email and tracking code are the identity the school has,
 * and the form's title is what the fee actually is.
 */

/** The FormSubmission arm of BillableUnion. */
/** Discriminated on applicant_email: the TranscriptRequest arm also carries a tracking_code, but never an email. */
type FormSubmissionBillable = Extract<NonNullable<BillLike['billable']>, { applicant_email?: unknown }>;

function asFormSubmission(billable: NonNullable<BillLike['billable']>): FormSubmissionBillable | null {
  return 'applicant_email' in billable || 'tracking_code' in billable
    ? (billable as FormSubmissionBillable)
    : null;
}

/** True for a bill raised against a form submission (an application fee). */
export function isFormSubmissionBill(bill: Pick<BillLike, 'billable_type'>): boolean {
  return bill.billable_type === 'FormSubmission';
}

/** The TranscriptRequest arm of BillableUnion: a transcript fee, owed by the person who asked for it. */
/** Its applicant_name / tracking_code are aliased in every document: both are non-null on TranscriptRequest but nullable on FormSubmission, and GraphQL rejects same-named fields of different nullability across union arms. */
type TranscriptRequestBillable = Extract<NonNullable<BillLike['billable']>, { request_applicant_name?: unknown }>;

/** True for a bill raised against a transcript request (a transcript fee). */
export function isTranscriptRequestBill(bill: Pick<BillLike, 'billable_type'>): boolean {
  return bill.billable_type === 'TranscriptRequest';
}

/**
 * The payer's name. An applicant is named from their submission's answers when
 * the form declares name fields (the same declaration the referee page and the
 * payment gateway read), falling back to the email they applied with, then the
 * tracking code they were given — never a bare row id when the bill still
 * knows it is an application.
 */
export function billableName(bill: BillLike): string {
  const billable = bill.billable;

  if (!billable) return unidentifiedBillable(bill);

  if (isTranscriptRequestBill(bill)) {
    const request = billable as TranscriptRequestBillable;
    const applicant = typeof request.request_applicant_name === 'string' ? request.request_applicant_name.trim() : '';
    const trackingCode = typeof request.request_tracking_code === 'string' ? request.request_tracking_code : '';
    return applicant || trackingCode || unidentifiedBillable(bill);
  }

  const submission = asFormSubmission(billable);
  if (submission) {
    return (
      submission.applicant_name ||
      submission.applicant_email ||
      submission.tracking_code ||
      unidentifiedBillable(bill)
    );
  }

  if ('first_name' in billable) return `${billable.first_name} ${billable.surname}`;
  // A class with no section name reads "400", not "400 - " or "400 - undefined".
  if ('classroom' in billable) return [billable.classroom.name, billable.section?.name].filter(Boolean).join(' - ');
  if ('program_name' in billable) return `${billable.program_name} (${billable.prog_code})`;

  return 'name' in billable ? billable.name : unidentifiedBillable(bill);
}

/**
 * The secondary line under the name. Every other billable keeps printing its
 * own type, which already reads as a word; only an application fee gets the
 * form's title, because "FormSubmission" tells an operator nothing about which
 * fee was charged.
 */
export function billableTypeLabel(bill: BillLike): string {
  if (isTranscriptRequestBill(bill)) return 'Transcript request';
  if (!isFormSubmissionBill(bill)) return bill.billable_type;

  const submission = bill.billable ? asFormSubmission(bill.billable) : null;

  return submission?.form?.title || 'Application fee';
}

/** Where the applicant's submission is reviewed. */
export type FormSubmissionRoute = {
  name: 'admin-forms-applicant-review';
  params: { id: string; submissionId: string };
};

/**
 * The application behind the bill, for a reader allowed to open it.
 *
 * Returns null whenever the jump cannot be made honestly — a non-form bill, a
 * submission that has been deleted, or a form the selection set did not carry.
 * The caller still has to check the reader's Forms permission: this only says
 * the destination exists, never that it may be shown.
 */
export function formSubmissionRoute(bill: BillLike): FormSubmissionRoute | null {
  if (!isFormSubmissionBill(bill) || !bill.billable) return null;

  const submission = asFormSubmission(bill.billable);
  const formId = submission?.form?.id;

  if (!submission?.id || !formId) return null;

  return {
    name: 'admin-forms-applicant-review',
    params: { id: String(formId), submissionId: String(submission.id) },
  };
}

/**
 * Whether this bill is owed by a cohort rather than one payer.
 *
 * Two different questions hide behind "indirect". For *discounts* it means the
 * amounts are shared and cannot be edited per line — an application fee counts,
 * and `isDirectBill` is the reader for that. For *presentation* it means the
 * money has to be summed across a roster, and there an application fee does
 * not count: it has exactly one payer, so the bill's own amount, net and
 * balance already are that person's figures.
 *
 * Reading the roster question off billable_type alone is what put six zeroes on
 * an application fee's detail page — a form submission has no student cohort,
 * so every total summed to nothing while the line items showed the real money.
 */
export function isCohortBill(bill: Pick<BillLike, 'billable_type'>): boolean {
  return bill.billable_type !== 'Student' && !isFormSubmissionBill(bill) && !isTranscriptRequestBill(bill);
}

/** What has gone, by the morph alias the bill still stores. */
const DELETED_PAYER: Record<string, string> = {
  Student: 'Deleted student',
  Classroom: 'Deleted class',
  ClassroomSection: 'Deleted class section',
  ProgrammeType: 'Deleted programme',
  FormSubmission: 'Deleted application',
  TranscriptRequest: 'Deleted transcript request',
};

/**
 * The last resort, still saying what kind of thing is missing. A payer that has
 * been deleted leaves its bill behind; "Deleted student" tells an operator what
 * happened, where "ID: 1936" named nothing they could look up. The bill's own
 * invoice number is the trace, so no row id is printed here.
 */
function unidentifiedBillable(bill: Pick<BillLike, 'billable_type'>): string {
  const alias = (bill.billable_type ?? '').split('\\').pop() ?? '';
  return DELETED_PAYER[alias] ?? 'Deleted payer';
}
